import { Button, makeStyles, mergeClasses, Text, tokens } from '@fluentui/react-components';
import { Checkmark16Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { t } from 'i18next';
import { useChat } from '../../libs/hooks/useChat';
import { useRequestNewChat } from '../../libs/hooks/useRequestNewChat';
import { useMessageFeedback } from '../../libs/hooks/useMessageFeedback';
import { useAppSelector } from '../../redux/app/hooks';
import { RootState } from '../../redux/app/store';
import { AppState } from '../../AppState';
import Icon from '../utils/Icon';
import { useAttachment } from '../../contexts/AttachmentContext';
import { botAnswerFeedbackState, UserFeedback } from '../../libs/models/ChatMessage';
import { postMessageToParent } from '../../libs/messaging';
import { notifyPanelHidden } from '../../libs/panelVisibility';
import { getParentTargetOrigin } from '../../libs/parentOrigin';

// Safety margin (px) kept between the reported drag zone and the buttonContainer's left edge, so
// the handle never hugs it exactly (rounding, or a click that lands right on the boundary).
const DRAG_ZONE_MARGIN_PX = 8;

// How long the "Tack!" beat stays up. The fade-in/hold/fade-out keyframes below run over the same
// window, and the clear-timer in the component uses it too, so the beat is invisible by unmount.
const THANKS_DURATION_MS = 2500;

const useStyles = makeStyles({
    dragger: {
        width: '100%',
        height: '50px',
        flexShrink: 0,
        zIndex: 999,
        fontWeight: 'bold',
        paddingLeft: tokens.spacingHorizontalL,
        paddingRight: tokens.spacingHorizontalL,
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxSizing: 'border-box',
        borderTopLeftRadius: tokens.borderRadiusXLarge,
        borderTopRightRadius: tokens.borderRadiusXLarge,
        boxShadow: tokens.shadow2,
    },
    embeddedHeader: {
        background: tokens.colorNeutralBackground1,
        color: tokens.colorNeutralForeground1,
    },
    headerText: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase400,
        margin: '0',
        fontWeight: tokens.fontWeightBold,
        userSelect: 'none', // Prevents text selection while dragging
    },
    buttonContainer: {
        color: tokens.colorNeutralForeground1,
        fontSize: tokens.fontSizeBase200,
        position: 'relative', // Ensure it's above the drag area
        zIndex: 2,
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
    },
    buttonIcon: {
        '&:hover': {
            cursor: 'pointer',
        },
        '&:first-child': {
            marginRight: tokens.spacingHorizontalXS,
        },
        '&:last-child': {
            marginLeft: tokens.spacingHorizontalXS,
        },
        '&:not(:last-child):not(:first-child)': {
            marginRight: tokens.spacingHorizontalXS,
            marginLeft: tokens.spacingHorizontalXS,
        },
    },
    // Inline "Hjälpte detta svaret dig?" pill, sits left of the new-chat / close icons.
    // Sentence case, neutral-weight Ja/Nej (no green/red) so we don't bias the answer.
    feedbackPill: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXS,
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalXXS,
        paddingTop: '2px',
        paddingBottom: '2px',
        marginRight: tokens.spacingHorizontalXS,
        borderRadius: tokens.borderRadiusCircular,
        // primary-100 lavender. colorBrandBackground2 maps to the brand ramp's lightest shade
        // (#fefeff ≈ white) in spirisLightTheme, so the pill was invisible; use the design
        // system's primary-100 surface (exposed as a CSS var by useAppTheme) instead.
        backgroundColor: 'var(--app-nav-tab-hover-background)',
    },
    feedbackPrompt: {
        color: tokens.colorNeutralForeground2,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        whiteSpace: 'nowrap',
        userSelect: 'none',
    },
    feedbackButton: {
        minWidth: 'auto',
        paddingLeft: tokens.spacingHorizontalS,
        paddingRight: tokens.spacingHorizontalS,
        // Fully rounded chip corners to echo the circular feedback pill. The design system has no
        // radius token between XLarge (8px) and Circular, so this is the next step up on-system.
        borderRadius: tokens.borderRadiusCircular,
        // Outline buttons are transparent by default, so the lavender pill shows through. Force a
        // white face so Nej/Ja read as white chips on the pill.
        backgroundColor: tokens.colorNeutralBackground1,
        '&:hover': {
            backgroundColor: tokens.colorNeutralBackground1Hover,
        },
        '&:active': {
            backgroundColor: tokens.colorNeutralBackground1Pressed,
        },
    },
    thanks: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: tokens.spacingHorizontalXXS,
        marginRight: tokens.spacingHorizontalXS,
        color: tokens.colorPaletteGreenForeground1,
        fontSize: tokens.fontSizeBase200,
        fontWeight: tokens.fontWeightRegular,
        whiteSpace: 'nowrap',
        userSelect: 'none',
        // Brief "Tack!" beat: fade in, hold, then fade out over the same window the clear-timer
        // uses (THANKS_DURATION_MS) so it's already invisible by the time it unmounts.
        animationName: {
            '0%': { opacity: 0 },
            '12%': { opacity: 1 },
            '75%': { opacity: 1 },
            '100%': { opacity: 0 },
        },
        animationDuration: `${String(THANKS_DURATION_MS)}ms`,
        animationTimingFunction: 'ease',
        animationFillMode: 'forwards',
        '@media (prefers-reduced-motion: reduce)': {
            animationName: 'none',
        },
    },
});

interface IChatRoomInlineHeaderProps {
    setAppState: React.Dispatch<React.SetStateAction<AppState>>;
    isVisible?: boolean;
}

export const ChatRoomInlineHeader: React.FC<IChatRoomInlineHeaderProps> = ({ setAppState, isVisible = true }) => {
    const classes = useStyles();
    const chat = useChat();
    const hostContext = useAppSelector((state: RootState) => state.hostContext.hostContext);
    const { setActiveAttachment } = useAttachment();

    const selectedId = useAppSelector((state: RootState) => state.conversations.selectedId);

    // Select the raw messages array and the streaming flag with separate, reference-stable
    // selectors, then derive everything else in useMemo. Returning a fresh object from one
    // useAppSelector would re-render the header on every Redux dispatch — even unrelated ones, and
    // very frequently during streaming — because useSelector compares results with ===. The
    // messages array keeps a stable reference (Immer structural sharing) until the messages
    // themselves change, and selectedId can briefly be set before the conversation hydrates so the
    // lookups are guarded.
    const messages = useAppSelector((state: RootState) => {
        const conv = selectedId ? state.conversations.conversations[selectedId] : undefined;
        return conv?.messages;
    });
    const isStreaming = useAppSelector((state: RootState) => {
        const conv = selectedId ? state.conversations.conversations[selectedId] : undefined;
        return Boolean(conv?.botResponseStatus);
    });

    // Most recent bot answer and whether a human agent is handling the conversation — both derived
    // by the shared botAnswerFeedbackState helper so the handover semantics stay in one tested
    // place next to isAgentSessionActive. Memoized on the (reference-stable) messages array so it
    // only re-runs when the messages actually change.
    const { latestAnswer: lastBotMessage, agentHandling } = useMemo(
        () => botAnswerFeedbackState(messages ?? []),
        [messages],
    );

    // Track the message we just answered so we can show a brief "Tack!" beat. Once feedback is
    // recorded on the message itself, the prompt won't reappear for it on reload/reopen.
    const [thankedMessageId, setThankedMessageId] = useState<string | undefined>(undefined);

    // The "Tack!" beat is transient: clear it after the fade-out finishes so it doesn't linger.
    // (The rated answer's own prompt stays hidden via its userFeedback, so without this the beat
    // would stay up until a brand-new bot answer arrives.) Re-runs only when a new message is
    // thanked.
    useEffect(() => {
        if (!thankedMessageId) {
            return;
        }
        const timer = setTimeout(() => {
            setThankedMessageId(undefined);
        }, THANKS_DURATION_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [thankedMessageId]);

    const requestNewChat = useRequestNewChat();
    const onNewChat = useCallback(() => {
        requestNewChat(() =>
            chat.createChat(hostContext).then(() => {
                setActiveAttachment(null, 'closed');
                setAppState(AppState.SettingUserInfo);
            }),
        );
    }, [requestNewChat, chat, setAppState, hostContext, setActiveAttachment]);

    const handleExit = useCallback(() => {
        // Also told by the host's `panelHidden`, but closing must not depend on that round trip.
        notifyPanelHidden();
        // CWE-668 (#2113): pin the validated embedder origin instead of '*' (see libs/parentOrigin.ts).
        window.parent.postMessage(
            {
                event: 'closeIframe',
            },
            getParentTargetOrigin(),
        );
    }, []);

    // Header Ja/Nej records the rating via the shared useMessageFeedback hook (the same #1566
    // optimistic-flip + rollback contract the in-message thumbs use). Unlike the thumbs it does NOT
    // open the optional-comment dialog — clicking Ja/Nej should just register the answer and show
    // the brief "Tack!" beat — and it opts into alerting on any failure so a write that didn't land
    // never silently looks saved. On success the answered message gets the transient beat.
    const recordFeedback = useMessageFeedback({
        source: 'header',
        alertOnAnyError: true,
        onSuccess: setThankedMessageId,
    });
    const onFeedback = useCallback(
        (positive: boolean) => {
            if (lastBotMessage) {
                recordFeedback(lastBotMessage, positive);
            }
        },
        [recordFeedback, lastBotMessage],
    );

    // Show the prompt for every finished bot answer that hasn't been rated yet — rating one answer
    // must not silence the prompt for the rest of the chat (#2266), since the answers that follow
    // are exactly the ones we have no feedback on. The per-message userFeedback check keeps us from
    // re-asking about an answer the user already rated, on reload and reopen too. Stay quiet while
    // streaming and while a human agent is handling the chat (the answer isn't the bot's).
    const showPrompt = useMemo(() => {
        if (isStreaming || agentHandling || !lastBotMessage?.id) {
            return false;
        }
        const fb = lastBotMessage.userFeedback;
        return fb === undefined || fb === UserFeedback.Unknown || fb === UserFeedback.Requested;
    }, [isStreaming, agentHandling, lastBotMessage]);

    // The "Tack!" beat only persists for the message the user just answered in this session.
    const showThanks = Boolean(thankedMessageId && lastBotMessage?.id === thankedMessageId);

    const headerRef = useRef<HTMLDivElement>(null);
    const buttonContainerRef = useRef<HTMLDivElement>(null);

    // Tell the host page how much of the header (from its left edge) is safe to drag from. The
    // host's drag-handle overlay lives outside the iframe, so it can't measure this itself — it
    // used to guess a fixed width, which either covered the Nej/Ja buttons (#2073) or left most of
    // the empty header dead (#2077). Re-measured on any resize of the header or the button cluster
    // (window resize, locale, or the feedback pill/Tack! beat appearing and disappearing).
    useLayoutEffect(() => {
        const header = headerRef.current;
        const buttonContainer = buttonContainerRef.current;
        // jsdom (Jest) doesn't implement ResizeObserver, so any test that renders this component
        // embedded would otherwise crash with a ReferenceError.
        if (!isVisible || !header || !buttonContainer || typeof ResizeObserver === 'undefined') {
            return;
        }

        const reportDragZoneWidth = () => {
            const width = Math.max(
                0,
                buttonContainer.getBoundingClientRect().left -
                    header.getBoundingClientRect().left -
                    DRAG_ZONE_MARGIN_PX,
            );
            postMessageToParent({ event: 'dragZoneWidth', data: { width } });
        };

        const observer = new ResizeObserver(reportDragZoneWidth);
        observer.observe(header);
        observer.observe(buttonContainer);
        return () => {
            observer.disconnect();
        };
    }, [isVisible]);

    if (!isVisible) return null;

    return (
        <div ref={headerRef} className={mergeClasses(classes.dragger, classes.embeddedHeader)}>
            <div>
                <Text as="h1" className={classes.headerText}>
                    {t('app.title')}
                </Text>
            </div>
            <div ref={buttonContainerRef} className={classes.buttonContainer}>
                {showThanks ? (
                    <span className={classes.thanks} role="status" aria-live="polite">
                        <Checkmark16Regular aria-hidden />
                        {t('inlineHeaderFeedback.thanks')}
                    </span>
                ) : (
                    showPrompt && (
                        <div className={classes.feedbackPill}>
                            <span className={classes.feedbackPrompt}>{t('inlineHeaderFeedback.prompt')}</span>
                            <Button
                                size="small"
                                appearance="outline"
                                className={classes.feedbackButton}
                                onClick={() => {
                                    onFeedback(false);
                                }}
                            >
                                {t('inlineHeaderFeedback.no')}
                            </Button>
                            <Button
                                size="small"
                                appearance="outline"
                                className={classes.feedbackButton}
                                onClick={() => {
                                    onFeedback(true);
                                }}
                            >
                                {t('inlineHeaderFeedback.yes')}
                            </Button>
                        </div>
                    )
                )}
                <Icon name="add-message" className={classes.buttonIcon} onClick={onNewChat} title={t('app.newChat')} />
                <Icon name="cancel" className={classes.buttonIcon} onClick={handleExit} title={t('app.closeChat')} />
            </div>
        </div>
    );
};
